import { FC } from "react";
import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { OverflowTooltip } from "src/domains/shared/components/OverflowTooltip";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { useTimezone } from "src/domains/shared/hooks/useTimezone";
import { formatInstant } from "src/domains/shared/utils/datetime";
import {
  isMemberWithdrawalEvent,
  resolveHistoryDisplayName,
  resolveHistoryTypeLabelKey,
  resolveTimeProjectBadge,
  withKoreanObjectParticle,
} from "src/domains/myPageContent/projectHistory/history-query/projectHistory.selectors";
import {
  hasHistoryType,
  HistoryEvent,
  KNOWN_HISTORY_TYPES,
} from "src/domains/myPageContent/projectHistory/history-query/projectHistory.model";
import { CAPTION, HOVER_SURFACE, HOVER_TRANSITION_MS, REDUCED_MOTION_SX } from "src/domains/shared/components/StateTransition";
import { MYPAGE_DIRTY_DOT, MYPAGE_ROW_HOVER_SURFACE } from "../myPageStyles";

interface HistoryTimelineItemProps {
  event: HistoryEvent;
}

/** `resolveHistoryTypeLabelKey` 가 반환하는(알려진 type 한정) i18n 키 — t() 타입 단언용. */
type HistoryTypeLabelI18nKey =
  `myPage.history.type.${(typeof KNOWN_HISTORY_TYPES)[number]}`;

/** `{ actor, target }` 을 받는 문장 키 — 종류 라벨과 이름을 `종류 "이름"` 한 덩어리로 받는다. */
type TargetFormSentenceI18nKey =
  | "myPage.history.sentence.create"
  | "myPage.history.sentence.register"
  | "myPage.history.sentence.delete"
  | "myPage.history.sentence.generic";

/**
 * 문장 템플릿 하나 — i18n 키와 **그 키가 받는 interpolation 계약**을 함께 들고 다닌다.
 *
 * - `targetForm`: `{ actor, target }`.
 * - `resultForm`: `{ actor, type, typeWithKoreanParticle, name }` — 수정 "후" 값 스냅샷을
 *   결과값 명시형으로 쓰기 위해 종류 라벨·이름을 분리해 넘긴다. 현재 `sentence.update` 전용이다.
 * - `actorOnly`: `{ actor }` — 대상이 행위자 자신이라 대상을 따로 말하지 않는 문장.
 *   현재 탈퇴 이탈(`sentence.memberWithdrawn`) 전용이다.
 */
type SentenceTemplate =
  | { kind: "targetForm"; key: TargetFormSentenceI18nKey }
  | { kind: "resultForm"; key: "myPage.history.sentence.update" }
  | { kind: "actorOnly"; key: "myPage.history.sentence.memberWithdrawn" };

/**
 * 탈퇴 이탈 전용 문장 — 행위자 = 대상 = 탈퇴자라 "누가 나갔다" 만 말한다. 높임 '-시-' 는 이 문장에만 쓴다.
 * `<type>:<action>` 특례 표보다 먼저 본다 — 같은 `member:delete` 조합을 id 비교로 가르기 때문이다.
 */
const MEMBER_WITHDRAWN_TEMPLATE: SentenceTemplate = {
  kind: "actorOnly",
  key: "myPage.history.sentence.memberWithdrawn",
};

/** 미지 action 폴백 — action 어휘가 열려 있어 매칭이 모두 실패할 수 있다. */
const GENERIC_SENTENCE_TEMPLATE: SentenceTemplate = {
  kind: "targetForm",
  key: "myPage.history.sentence.generic",
};

/** action(create/update/delete)별 문장 템플릿 — 열린 어휘라 미지 action 은 generic 으로 폴백. */
const SENTENCE_TEMPLATE_BY_ACTION: Record<string, SentenceTemplate> = {
  create: { kind: "targetForm", key: "myPage.history.sentence.create" },
  // update 의 name 은 수정 "후" 값 스냅샷이라 유일하게 결과값 명시형 문장을 쓴다.
  update: { kind: "resultForm", key: "myPage.history.sentence.update" },
  delete: { kind: "targetForm", key: "myPage.history.sentence.delete" },
};

/**
 * `<history.type>:<action>` 문장 특례 — 백엔드·운영이 기본 동사와 다르게 부르는 조합만 둔다.
 * 댓글 이미지는 서버가 "등록 ↔ 삭제" 한 쌍으로 정의한 이벤트라 create 를 "등록" 문구로 쓴다
 * (issue-system#455). 여기에 없는 조합은 `SENTENCE_TEMPLATE_BY_ACTION` 으로 폴백한다.
 */
const SENTENCE_TEMPLATE_BY_TYPE_AND_ACTION: Record<string, SentenceTemplate> = {
  "annotation_comment_image:create": {
    kind: "targetForm",
    key: "myPage.history.sentence.register",
  },
};

/**
 * 동작 표시 점 색 — 추가·등록 / 수정 / 삭제. 문장이 동작을 함께 말하므로 색은 보조 신호다.
 * 열린 어휘라 미지 action 은 중립색.
 */
const ACTION_DOT_COLOR: Record<string, string> = {
  create: COLOR_CONSTANTS.primary.main,
  update: MYPAGE_DIRTY_DOT,
  delete: COLOR_CONSTANTS.error.main,
};
const NEUTRAL_DOT_COLOR = "rgba(0, 0, 0, 0.26)";

/**
 * 소유 프로퍼티만 조회한다 — history.type·action 모두 서버 열린 어휘라
 * "constructor" 같은 프로토타입 키가 템플릿·색으로 새는 것을 막는다.
 */
const lookupOwn = <T,>(map: Record<string, T>, key: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

/**
 * 이벤트의 종류 라벨(`typeLabel`)과 표시 이름(`displayName`)을 지역화한다.
 * 이름은 `time_project` · `walk_session` 두 type 만 i18n 템플릿으로 장식하고,
 * 나머지 type 은 `resolveHistoryDisplayName` 의 원시 이름을 그대로 쓴다.
 * `member` 의 빈 이름(탈퇴한 멤버의 이메일)은 공용 라벨로 바꾼다 — 다른 type 의 이름이 비는 경우는 계약에 없다.
 */
const resolveLocalizedLabels = (
  event: HistoryEvent,
  t: TFunction<"projects">,
  unknownUserLabel: string
): { typeLabel: string; displayName: string } => {
  const typeLabelKey = resolveHistoryTypeLabelKey(event.history.type);
  const typeLabel = typeLabelKey
    ? t(typeLabelKey as HistoryTypeLabelI18nKey, { ns: "projects" })
    : event.history.type;

  const rawDisplayName = resolveHistoryDisplayName(event);
  if (hasHistoryType(event.history, "member")) {
    return { typeLabel, displayName: rawDisplayName || unknownUserLabel };
  }
  if (hasHistoryType(event.history, "time_project")) {
    return {
      typeLabel,
      displayName: t("myPage.history.name.timeProject", { name: rawDisplayName, ns: "projects" }),
    };
  }
  if (hasHistoryType(event.history, "walk_session")) {
    return {
      typeLabel,
      displayName: t("myPage.history.name.walkSession", { name: rawDisplayName, ns: "projects" }),
    };
  }
  return { typeLabel, displayName: rawDisplayName };
};

/** action 에 맞는 문장 템플릿으로 "누가 무엇을 했다" 한 문장을 조립한다. */
const resolveSentence = (
  event: HistoryEvent,
  typeLabel: string,
  displayName: string,
  t: TFunction<"projects">,
  unknownUserLabel: string
): string => {
  const template = isMemberWithdrawalEvent(event)
    ? MEMBER_WITHDRAWN_TEMPLATE
    : (lookupOwn(SENTENCE_TEMPLATE_BY_TYPE_AND_ACTION, `${event.history.type}:${event.action}`) ??
      lookupOwn(SENTENCE_TEMPLATE_BY_ACTION, event.action) ??
      GENERIC_SENTENCE_TEMPLATE);
  // 탈퇴한 행위자는 이름 스냅샷이 빈 문자열로 온다 — 문장 틀은 그대로 두고 이름 자리만 공용 라벨로 채운다.
  const actor = event.actor.name || unknownUserLabel;

  if (template.kind === "actorOnly") {
    return t(template.key, { actor, ns: "projects" });
  }
  // 결과값 명시형은 종류 라벨·이름을 분리해 넘긴다 — ko 는 라벨 뒤 을/를을 받침으로 자동 선택한다.
  if (template.kind === "resultForm") {
    return t(template.key, {
      actor,
      type: typeLabel,
      typeWithKoreanParticle: withKoreanObjectParticle(typeLabel),
      name: displayName,
      ns: "projects",
    });
  }
  return t(template.key, {
    actor,
    target: `${typeLabel} "${displayName}"`,
    ns: "projects",
  });
};

/**
 * 회차 귀속 뱃지 문구 — 촬영 일자 스냅샷이 있으면 일자 표기, 없으면 회차 id 폴백 표기.
 * 비귀속 이벤트는 null(뱃지 미표시).
 */
const resolveBadgeText = (event: HistoryEvent, t: TFunction<"projects">): string | null => {
  const timeProjectBadge = resolveTimeProjectBadge(event);
  if (timeProjectBadge?.kind === "timestamp") {
    return t("myPage.history.name.timeProject", { name: timeProjectBadge.value, ns: "projects" });
  }
  if (timeProjectBadge?.kind === "timeProjectId") {
    return t("myPage.history.timeProjectFallback", { id: timeProjectBadge.value, ns: "projects" });
  }
  return null;
};

/**
 * 히스토리 이벤트 1건을 표시용 뷰 모델(문장 · 발생 시각 · 뱃지 · 점 색)로 조립한다.
 * i18n `t()` 는 UI 계층 소유 원칙에 따라 순수 selector 가 아닌 이 훅(컴포넌트 계층)에 둔다.
 */
function useHistoryEventView(event: HistoryEvent) {
  const { t } = useTranslation("projects");
  const { timezone } = useTimezone();

  // 탈퇴한 사람의 공용 라벨 — 화면 전체가 같은 키 하나를 쓴다(withdrawn-user-display).
  const unknownUserLabel = t("user.unknown", { ns: "common" });
  const { typeLabel, displayName } = resolveLocalizedLabels(event, t, unknownUserLabel);
  return {
    sentence: resolveSentence(event, typeLabel, displayName, t, unknownUserLabel),
    time: formatInstant(event.occurred_at, "HH:mm", { tz: timezone }),
    badgeText: resolveBadgeText(event, t),
    dotColor: lookupOwn(ACTION_DOT_COLOR, event.action) ?? NEUTRAL_DOT_COLOR,
  };
}

const ROW_HEIGHT_PX = 40;

/** 이력 한 줄 — 시각 · 동작 점 · 문장(잘리면 아래 툴팁) · 회차 뱃지. */
export const HistoryTimelineItem: FC<HistoryTimelineItemProps> = ({ event }) => {
  const { sentence, time, badgeText, dotColor } = useHistoryEventView(event);

  return (
    <Box
      component="li"
      sx={{
        listStyle: "none",
        height: ROW_HEIGHT_PX,
        display: "grid",
        gridTemplateColumns: "44px 8px minmax(0, 1fr) auto",
        alignItems: "center",
        columnGap: "10px",
        padding: "0 16px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
        transition: `background-color ${HOVER_TRANSITION_MS}ms ease-out`,
        "&:hover": { backgroundColor: MYPAGE_ROW_HOVER_SURFACE },
        ...REDUCED_MOTION_SX,
      }}
    >
      <Typography
        component="time"
        dateTime={event.occurred_at}
        sx={{ fontSize: 12, color: CAPTION, fontVariantNumeric: "tabular-nums" }}
      >
        {time}
      </Typography>
      <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dotColor }} />
      <OverflowTooltip text={sentence} placement="bottom-start" sx={{ fontSize: 13, fontWeight: 500 }} />
      {badgeText ? (
        <Typography
          sx={{
            height: 18,
            padding: "0 6px",
            borderRadius: "4px",
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            fontSize: 11,
            fontWeight: 500,
            fontVariantNumeric: "tabular-nums",
            // 회차 귀속은 보조 정보라 문장보다 눈에 띄지 않게 중립색으로 둔다.
            color: CAPTION,
            backgroundColor: HOVER_SURFACE,
          }}
        >
          {badgeText}
        </Typography>
      ) : (
        <Box />
      )}
    </Box>
  );
};
